import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, MessageCircle, Phone, Zap, FileText, DollarSign, ArrowRight, Minus, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tüm 11 dil için çeviriler
const translations = {
  en: {
    title: 'PIRONE AI',
    subtitle: 'Digital Assistant',
    greeting: "Hello! How can I help you today?",
    placeholder: 'Type your message...',
    send: 'Send',
    pricing: 'Pricing',
    services: 'Services',
    pricingQuery: 'What are your prices?',
    servicesQuery: 'What services do you offer?',
    getQuote: 'Get Quick Quote',
    contactCta: 'Contact Us',
    quoteTitle: 'Quick Quote Form',
    quoteSubtitle: "We'll get back to you within 24 hours",
    formName: 'Your Name',
    formEmail: 'E-mail',
    formPhone: 'Phone',
    formProjectType: 'Project Type',
    formBudget: 'Budget Range',
    formDescription: 'Project Details',
    formSubmit: 'Request Quote',
    formSending: 'Sending...',
    quoteSuccess: 'Request received!',
    quoteSuccessText: "We'll contact you within 24 hours.",
    backToChat: 'Back to Chat',
    projectTypes: ['Website', 'E-Commerce', 'Web App', 'Mobile App', 'AI Integration', 'Other'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: 'Connection error. Please try again.',
    error: 'An error occurred.'
  },
  tr: {
    title: 'PIRONE AI',
    subtitle: 'Dijital Asistan',
    greeting: "Merhaba! Size nasıl yardımcı olabilirim?",
    placeholder: 'Mesajınızı yazın...',
    send: 'Gönder',
    pricing: 'Fiyatlar',
    services: 'Hizmetler',
    pricingQuery: 'Fiyatlarınız nedir?',
    servicesQuery: 'Hangi hizmetleri sunuyorsunuz?',
    getQuote: 'Hızlı Teklif Al',
    contactCta: 'İletişime Geç',
    quoteTitle: 'Hızlı Teklif Formu',
    quoteSubtitle: '24 saat içinde size dönüş yapacağız',
    formName: 'Adınız',
    formEmail: 'E-posta',
    formPhone: 'Telefon',
    formProjectType: 'Proje Türü',
    formBudget: 'Bütçe Aralığı',
    formDescription: 'Proje Detayları',
    formSubmit: 'Teklif İste',
    formSending: 'Gönderiliyor...',
    quoteSuccess: 'Talebiniz alındı!',
    quoteSuccessText: '24 saat içinde size döneceğiz.',
    backToChat: 'Sohbete Dön',
    projectTypes: ['Web Sitesi', 'E-Ticaret', 'Web Uygulama', 'Mobil Uygulama', 'AI Entegrasyon', 'Diğer'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: 'Bağlantı hatası. Tekrar deneyin.',
    error: 'Bir hata oluştu.'
  },
  es: {
    title: 'PIRONE AI',
    subtitle: 'Asistente Digital',
    greeting: "¡Hola! ¿Cómo puedo ayudarte hoy?",
    placeholder: 'Escribe tu mensaje...',
    send: 'Enviar',
    pricing: 'Precios',
    services: 'Servicios',
    pricingQuery: '¿Cuáles son sus precios?',
    servicesQuery: '¿Qué servicios ofrecen?',
    getQuote: 'Solicitar Cotización',
    contactCta: 'Contáctenos',
    quoteTitle: 'Formulario de Cotización',
    quoteSubtitle: 'Le responderemos en 24 horas',
    formName: 'Su Nombre',
    formEmail: 'Correo',
    formPhone: 'Teléfono',
    formProjectType: 'Tipo de Proyecto',
    formBudget: 'Rango de Presupuesto',
    formDescription: 'Detalles del Proyecto',
    formSubmit: 'Solicitar Cotización',
    formSending: 'Enviando...',
    quoteSuccess: '¡Solicitud recibida!',
    quoteSuccessText: 'Le contactaremos en 24 horas.',
    backToChat: 'Volver al Chat',
    projectTypes: ['Sitio Web', 'E-Commerce', 'Aplicación Web', 'Aplicación Móvil', 'Integración AI', 'Otro'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: 'Error de conexión. Inténtelo de nuevo.',
    error: 'Ocurrió un error.'
  },
  pt: {
    title: 'PIRONE AI',
    subtitle: 'Assistente Digital',
    greeting: "Olá! Como posso ajudá-lo hoje?",
    placeholder: 'Digite sua mensagem...',
    send: 'Enviar',
    pricing: 'Preços',
    services: 'Serviços',
    pricingQuery: 'Quais são seus preços?',
    servicesQuery: 'Quais serviços vocês oferecem?',
    getQuote: 'Solicitar Orçamento',
    contactCta: 'Contate-nos',
    quoteTitle: 'Formulário de Orçamento',
    quoteSubtitle: 'Responderemos em 24 horas',
    formName: 'Seu Nome',
    formEmail: 'E-mail',
    formPhone: 'Telefone',
    formProjectType: 'Tipo de Projeto',
    formBudget: 'Faixa de Orçamento',
    formDescription: 'Detalhes do Projeto',
    formSubmit: 'Solicitar Orçamento',
    formSending: 'Enviando...',
    quoteSuccess: 'Solicitação recebida!',
    quoteSuccessText: 'Entraremos em contato em 24 horas.',
    backToChat: 'Voltar ao Chat',
    projectTypes: ['Site', 'E-Commerce', 'Aplicativo Web', 'Aplicativo Móvel', 'Integração AI', 'Outro'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: 'Erro de conexão. Tente novamente.',
    error: 'Ocorreu um erro.'
  },
  zh: {
    title: 'PIRONE AI',
    subtitle: '数字助手',
    greeting: "您好！我今天能帮您什么？",
    placeholder: '输入您的消息...',
    send: '发送',
    pricing: '价格',
    services: '服务',
    pricingQuery: '你们的价格是多少？',
    servicesQuery: '你们提供哪些服务？',
    getQuote: '获取报价',
    contactCta: '联系我们',
    quoteTitle: '快速报价表',
    quoteSubtitle: '我们将在24小时内回复您',
    formName: '您的姓名',
    formEmail: '电子邮件',
    formPhone: '电话',
    formProjectType: '项目类型',
    formBudget: '预算范围',
    formDescription: '项目详情',
    formSubmit: '提交报价请求',
    formSending: '发送中...',
    quoteSuccess: '请求已收到！',
    quoteSuccessText: '我们将在24小时内与您联系。',
    backToChat: '返回聊天',
    projectTypes: ['网站', '电子商务', '网页应用', '移动应用', 'AI集成', '其他'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: '连接错误，请重试。',
    error: '发生错误。'
  },
  ar: {
    title: 'PIRONE AI',
    subtitle: 'المساعد الرقمي',
    greeting: "مرحباً! كيف يمكنني مساعدتك اليوم؟",
    placeholder: 'اكتب رسالتك...',
    send: 'إرسال',
    pricing: 'الأسعار',
    services: 'الخدمات',
    pricingQuery: 'ما هي أسعاركم؟',
    servicesQuery: 'ما هي الخدمات التي تقدمونها؟',
    getQuote: 'احصل على عرض سعر',
    contactCta: 'تواصل معنا',
    quoteTitle: 'نموذج عرض السعر',
    quoteSubtitle: 'سنرد عليك خلال 24 ساعة',
    formName: 'اسمك',
    formEmail: 'البريد الإلكتروني',
    formPhone: 'الهاتف',
    formProjectType: 'نوع المشروع',
    formBudget: 'نطاق الميزانية',
    formDescription: 'تفاصيل المشروع',
    formSubmit: 'طلب عرض سعر',
    formSending: 'جاري الإرسال...',
    quoteSuccess: 'تم استلام الطلب!',
    quoteSuccessText: 'سنتواصل معك خلال 24 ساعة.',
    backToChat: 'العودة للمحادثة',
    projectTypes: ['موقع ويب', 'تجارة إلكترونية', 'تطبيق ويب', 'تطبيق جوال', 'تكامل AI', 'أخرى'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: 'خطأ في الاتصال. حاول مرة أخرى.',
    error: 'حدث خطأ.'
  },
  hi: {
    title: 'PIRONE AI',
    subtitle: 'डिजिटल सहायक',
    greeting: "नमस्ते! मैं आज आपकी कैसे मदद कर सकता हूं?",
    placeholder: 'अपना संदेश लिखें...',
    send: 'भेजें',
    pricing: 'मूल्य',
    services: 'सेवाएं',
    pricingQuery: 'आपकी कीमतें क्या हैं?',
    servicesQuery: 'आप कौन सी सेवाएं प्रदान करते हैं?',
    getQuote: 'त्वरित उद्धरण प्राप्त करें',
    contactCta: 'संपर्क करें',
    quoteTitle: 'त्वरित उद्धरण फॉर्म',
    quoteSubtitle: 'हम 24 घंटे के भीतर आपसे संपर्क करेंगे',
    formName: 'आपका नाम',
    formEmail: 'ईमेल',
    formPhone: 'फोन',
    formProjectType: 'प्रोजेक्ट प्रकार',
    formBudget: 'बजट सीमा',
    formDescription: 'प्रोजेक्ट विवरण',
    formSubmit: 'उद्धरण अनुरोध करें',
    formSending: 'भेजा जा रहा है...',
    quoteSuccess: 'अनुरोध प्राप्त हुआ!',
    quoteSuccessText: 'हम 24 घंटे के भीतर आपसे संपर्क करेंगे।',
    backToChat: 'चैट पर वापस जाएं',
    projectTypes: ['वेबसाइट', 'ई-कॉमर्स', 'वेब ऐप', 'मोबाइल ऐप', 'AI एकीकरण', 'अन्य'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: 'कनेक्शन त्रुटि। पुनः प्रयास करें।',
    error: 'एक त्रुटि हुई।'
  },
  ko: {
    title: 'PIRONE AI',
    subtitle: '디지털 어시스턴트',
    greeting: "안녕하세요! 오늘 무엇을 도와드릴까요?",
    placeholder: '메시지를 입력하세요...',
    send: '보내기',
    pricing: '가격',
    services: '서비스',
    pricingQuery: '가격이 어떻게 되나요?',
    servicesQuery: '어떤 서비스를 제공하나요?',
    getQuote: '빠른 견적 받기',
    contactCta: '문의하기',
    quoteTitle: '빠른 견적 양식',
    quoteSubtitle: '24시간 이내에 연락드리겠습니다',
    formName: '이름',
    formEmail: '이메일',
    formPhone: '전화번호',
    formProjectType: '프로젝트 유형',
    formBudget: '예산 범위',
    formDescription: '프로젝트 상세',
    formSubmit: '견적 요청',
    formSending: '전송 중...',
    quoteSuccess: '요청이 접수되었습니다!',
    quoteSuccessText: '24시간 이내에 연락드리겠습니다.',
    backToChat: '채팅으로 돌아가기',
    projectTypes: ['웹사이트', '이커머스', '웹 앱', '모바일 앱', 'AI 통합', '기타'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: '연결 오류. 다시 시도해주세요.',
    error: '오류가 발생했습니다.'
  },
  el: {
    title: 'PIRONE AI',
    subtitle: 'Ψηφιακός Βοηθός',
    greeting: "Γεια σας! Πώς μπορώ να σας βοηθήσω σήμερα;",
    placeholder: 'Γράψτε το μήνυμά σας...',
    send: 'Αποστολή',
    pricing: 'Τιμές',
    services: 'Υπηρεσίες',
    pricingQuery: 'Ποιες είναι οι τιμές σας;',
    servicesQuery: 'Τι υπηρεσίες προσφέρετε;',
    getQuote: 'Λάβετε Προσφορά',
    contactCta: 'Επικοινωνήστε',
    quoteTitle: 'Φόρμα Προσφοράς',
    quoteSubtitle: 'Θα επικοινωνήσουμε εντός 24 ωρών',
    formName: 'Το Όνομά σας',
    formEmail: 'Email',
    formPhone: 'Τηλέφωνο',
    formProjectType: 'Τύπος Έργου',
    formBudget: 'Εύρος Προϋπολογισμού',
    formDescription: 'Λεπτομέρειες Έργου',
    formSubmit: 'Αίτηση Προσφοράς',
    formSending: 'Αποστολή...',
    quoteSuccess: 'Το αίτημα ελήφθη!',
    quoteSuccessText: 'Θα επικοινωνήσουμε εντός 24 ωρών.',
    backToChat: 'Επιστροφή στη Συνομιλία',
    projectTypes: ['Ιστοσελίδα', 'Ηλ. Κατάστημα', 'Web App', 'Mobile App', 'AI Integration', 'Άλλο'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: 'Σφάλμα σύνδεσης. Δοκιμάστε ξανά.',
    error: 'Προέκυψε σφάλμα.'
  },
  it: {
    title: 'PIRONE AI',
    subtitle: 'Assistente Digitale',
    greeting: "Ciao! Come posso aiutarti oggi?",
    placeholder: 'Scrivi il tuo messaggio...',
    send: 'Invia',
    pricing: 'Prezzi',
    services: 'Servizi',
    pricingQuery: 'Quali sono i vostri prezzi?',
    servicesQuery: 'Quali servizi offrite?',
    getQuote: 'Richiedi Preventivo',
    contactCta: 'Contattaci',
    quoteTitle: 'Modulo Preventivo',
    quoteSubtitle: 'Ti risponderemo entro 24 ore',
    formName: 'Il Tuo Nome',
    formEmail: 'Email',
    formPhone: 'Telefono',
    formProjectType: 'Tipo di Progetto',
    formBudget: 'Range di Budget',
    formDescription: 'Dettagli Progetto',
    formSubmit: 'Richiedi Preventivo',
    formSending: 'Invio...',
    quoteSuccess: 'Richiesta ricevuta!',
    quoteSuccessText: 'Ti contatteremo entro 24 ore.',
    backToChat: 'Torna alla Chat',
    projectTypes: ['Sito Web', 'E-Commerce', 'Web App', 'App Mobile', 'Integrazione AI', 'Altro'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: 'Errore di connessione. Riprova.',
    error: 'Si è verificato un errore.'
  },
  gu: {
    title: 'PIRONE AI',
    subtitle: 'ડિજિટલ સહાયક',
    greeting: "નમસ્તે! હું આજે તમને કેવી રીતે મદદ કરી શકું?",
    placeholder: 'તમારો સંદેશ લખો...',
    send: 'મોકલો',
    pricing: 'કિંમતો',
    services: 'સેવાઓ',
    pricingQuery: 'તમારી કિંમતો શું છે?',
    servicesQuery: 'તમે કઈ સેવાઓ આપો છો?',
    getQuote: 'ઝડપી ક્વોટ મેળવો',
    contactCta: 'સંપર્ક કરો',
    quoteTitle: 'ઝડપી ક્વોટ ફોર્મ',
    quoteSubtitle: 'અમે 24 કલાકમાં તમારો સંપર્ક કરીશું',
    formName: 'તમારું નામ',
    formEmail: 'ઇમેઇલ',
    formPhone: 'ફોન',
    formProjectType: 'પ્રોજેક્ટ પ્રકાર',
    formBudget: 'બજેટ શ્રેણી',
    formDescription: 'પ્રોજેક્ટ વિગતો',
    formSubmit: 'ક્વોટ માટે વિનંતી',
    formSending: 'મોકલી રહ્યા છીએ...',
    quoteSuccess: 'વિનંતી મળી!',
    quoteSuccessText: 'અમે 24 કલાકમાં તમારો સંપર્ક કરીશું.',
    backToChat: 'ચેટ પર પાછા જાઓ',
    projectTypes: ['વેબસાઇટ', 'ઇ-કોમર્સ', 'વેબ એપ', 'મોબાઇલ એપ', 'AI એકીકરણ', 'અન્ય'],
    budgetRanges: ['$1,000 - $3,000', '$3,000 - $8,000', '$8,000 - $15,000', '$15,000 - $30,000', '$30,000+'],
    connectionError: 'કનેક્શન ભૂલ. ફરી પ્રયાસ કરો.',
    error: 'એક ભૂલ થઈ.'
  }
};

const getContent = (lang) => translations[lang] || translations.en;

const PironeAIChat = ({ isOpen, onClose, onMinimize, isMinimized }) => {
  const { language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteData, setQuoteData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    description: ''
  });
  const [quoteSent, setQuoteSent] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const content = getContent(language);

  // Keywords that should trigger contact suggestion
  const contactTriggerKeywords = [
    'fiyat', 'price', 'cost', 'maliyet', 'ücret', 'fee', 'kaç para', 'how much',
    'detay', 'detail', 'özel', 'specific', 'custom', 'teklif', 'quote', 'proposal',
    '价格', '費用', 'precio', 'preço', 'τιμή', 'prezzo', 'سعر', 'कीमत', '가격'
  ];

  const shouldShowContactButton = (text) => {
    const lowerText = text.toLowerCase();
    return contactTriggerKeywords.some(keyword => lowerText.includes(keyword));
  };

  useEffect(() => {
    if (isOpen && !isMinimized && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: content.greeting,
        showContact: false
      }]);
    }
  }, [isOpen, isMinimized, language]);

  // Update greeting when language changes
  useEffect(() => {
    if (messages.length === 1 && messages[0].role === 'assistant') {
      setMessages([{
        role: 'assistant',
        content: content.greeting,
        showContact: false
      }]);
    }
  }, [language]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async (text) => {
    const messageText = text || input.trim();
    if (!messageText || isLoading) return;

    const userShouldShowContact = shouldShowContactButton(messageText);

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: messageText }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          query: messageText,
          context: 'pirone_assistant',
          language: language
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      let answerText = data.answer || content.error;
      const responseShouldShowContact = shouldShowContactButton(answerText) || userShouldShowContact;
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: answerText,
        showContact: responseShouldShowContact
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: content.connectionError,
        showContact: false
      }]);
    }

    setIsLoading(false);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: quoteData.name,
          email: quoteData.email,
          phone: quoteData.phone,
          company: quoteData.projectType,
          message: `[Quick Quote] Budget: ${quoteData.budget}\n\nProject Details:\n${quoteData.description}`,
          source: 'pirone-ai-quick-quote'
        })
      });
      setQuoteSent(true);
    } catch (error) {
      console.error(error);
    }

    setIsLoading(false);
  };

  const handleBackToChat = () => {
    setShowQuoteForm(false);
    setQuoteSent(false);
    setQuoteData({
      name: '',
      email: '',
      phone: '',
      projectType: '',
      budget: '',
      description: ''
    });
  };

  if (!isOpen) return null;

  // Minimized state
  if (isMinimized) {
    return (
      <motion.button
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={onMinimize}
        className="fixed bottom-6 right-6 z-[100] group"
      >
        <div className="relative flex items-center gap-2 h-12 px-4 bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-2xl hover:border-[#38BDF8]/30 transition-all">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#38BDF8] to-[#0EA5E9] flex items-center justify-center">
            <Sparkles size={16} className="text-white" />
          </div>
          <span className="text-sm font-medium text-white/80">PIRONE AI</span>
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        </div>
      </motion.button>
    );
  }

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-6 right-6 z-[100] w-[380px] max-w-[calc(100vw-48px)]"
        style={{ maxHeight: 'calc(100vh - 120px)' }}
      >
        <div 
          className="bg-[#0A0A0A] border border-white/[0.08] rounded-3xl flex flex-col overflow-hidden"
          style={{ 
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.6), 0 0 60px rgba(56, 189, 248, 0.08)',
            maxHeight: 'calc(100vh - 120px)'
          }}
        >
          {/* Header */}
          <div className="relative px-4 py-3 border-b border-white/[0.06] bg-gradient-to-r from-[#0A0A0A] to-[#0F0F0F]">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#38BDF8]/40 to-transparent" />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#38BDF8] to-[#0EA5E9] flex items-center justify-center shadow-lg shadow-[#38BDF8]/20">
                    <Sparkles size={20} className="text-white" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[#0A0A0A]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">{content.title}</h2>
                  <p className="text-[10px] text-white/40 flex items-center gap-1">
                    <Zap size={8} className="text-[#38BDF8]" />
                    {content.subtitle}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                <button onClick={onMinimize} className="w-8 h-8 rounded-lg hover:bg-white/[0.05] flex items-center justify-center transition-all">
                  <Minus size={14} className="text-white/40" />
                </button>
                <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-white/[0.05] flex items-center justify-center transition-all">
                  <X size={14} className="text-white/40" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {showQuoteForm ? (
            <div className="flex-1 overflow-auto p-4" style={{ maxHeight: '420px' }}>
              {quoteSent ? (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <Sparkles size={28} className="text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{content.quoteSuccess}</h3>
                  <p className="text-sm text-white/50 mb-6">{content.quoteSuccessText}</p>
                  <button onClick={handleBackToChat} className="inline-flex items-center gap-2 text-sm text-[#38BDF8] hover:underline">
                    <ArrowLeft size={14} />
                    {content.backToChat}
                  </button>
                </motion.div>
              ) : (
                <>
                  <button onClick={handleBackToChat} className="flex items-center gap-2 text-sm text-white/50 hover:text-white mb-4 transition-colors">
                    <ArrowLeft size={16} />
                    <span>{content.backToChat}</span>
                  </button>

                  <div className="text-center mb-4">
                    <h3 className="text-base font-semibold text-white">{content.quoteTitle}</h3>
                    <p className="text-xs text-white/40">{content.quoteSubtitle}</p>
                  </div>
                  
                  <form onSubmit={handleQuoteSubmit} className="space-y-3">
                    <input type="text" placeholder={content.formName} value={quoteData.name} onChange={(e) => setQuoteData({...quoteData, name: e.target.value})} required className="w-full h-10 px-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-white/30 focus:border-[#38BDF8]/40 focus:outline-none transition-all" />
                    
                    <div className="grid grid-cols-2 gap-2">
                      <input type="email" placeholder={content.formEmail} value={quoteData.email} onChange={(e) => setQuoteData({...quoteData, email: e.target.value})} required className="w-full h-10 px-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-white/30 focus:border-[#38BDF8]/40 focus:outline-none transition-all" />
                      <input type="tel" placeholder={content.formPhone} value={quoteData.phone} onChange={(e) => setQuoteData({...quoteData, phone: e.target.value})} className="w-full h-10 px-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-white/30 focus:border-[#38BDF8]/40 focus:outline-none transition-all" />
                    </div>

                    <select value={quoteData.projectType} onChange={(e) => setQuoteData({...quoteData, projectType: e.target.value})} required className="w-full h-10 px-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white/70 focus:border-[#38BDF8]/40 focus:outline-none transition-all appearance-none cursor-pointer" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'rgba(255,255,255,0.4)\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px' }}>
                      <option value="" disabled>{content.formProjectType}</option>
                      {content.projectTypes.map((type, i) => (
                        <option key={i} value={type} className="bg-[#0A0A0A]">{type}</option>
                      ))}
                    </select>

                    <select value={quoteData.budget} onChange={(e) => setQuoteData({...quoteData, budget: e.target.value})} required className="w-full h-10 px-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white/70 focus:border-[#38BDF8]/40 focus:outline-none transition-all appearance-none cursor-pointer" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'rgba(255,255,255,0.4)\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px' }}>
                      <option value="" disabled>{content.formBudget}</option>
                      {content.budgetRanges.map((range, i) => (
                        <option key={i} value={range} className="bg-[#0A0A0A]">{range}</option>
                      ))}
                    </select>

                    <textarea placeholder={content.formDescription} value={quoteData.description} onChange={(e) => setQuoteData({...quoteData, description: e.target.value})} rows={3} className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-white/30 focus:border-[#38BDF8]/40 focus:outline-none transition-all resize-none" />

                    <button type="submit" disabled={isLoading} className="w-full h-11 rounded-xl bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#38BDF8]/20 transition-all disabled:opacity-50">
                      {isLoading ? content.formSending : (<><FileText size={14} />{content.formSubmit}</>)}
                    </button>
                  </form>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto p-4 space-y-3" style={{ maxHeight: '280px', minHeight: '200px' }}>
                {messages.map((msg, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                    <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] px-3.5 py-2.5 text-[13px] leading-relaxed ${msg.role === 'user' ? 'bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] text-white rounded-2xl rounded-br-md' : 'bg-white/[0.04] border border-white/[0.06] text-white/90 rounded-2xl rounded-bl-md'}`}>
                        {msg.content}
                      </div>
                    </div>
                    
                    {msg.role === 'assistant' && msg.showContact && (
                      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start mt-2 ml-1">
                        <Link to="/contact" onClick={onClose} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#38BDF8]/10 border border-[#38BDF8]/30 text-[#38BDF8] text-[11px] font-semibold rounded-lg hover:bg-[#38BDF8]/20 transition-all">
                          <Phone size={12} />
                          {content.contactCta}
                        </Link>
                      </motion.div>
                    )}
                  </motion.div>
                ))}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="px-3.5 py-2.5 bg-white/[0.04] border border-white/[0.06] rounded-2xl rounded-bl-md">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-[#38BDF8] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-[#38BDF8] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-[#38BDF8] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {messages.length <= 1 && !isLoading && (
                <div className="px-4 pb-2">
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => sendMessage(content.pricingQuery)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-[11px] font-medium text-white/60 hover:text-white hover:border-[#38BDF8]/30 hover:bg-[#38BDF8]/5 transition-all">
                      <DollarSign size={12} />
                      {content.pricing}
                    </button>
                    <button onClick={() => sendMessage(content.servicesQuery)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-[11px] font-medium text-white/60 hover:text-white hover:border-[#38BDF8]/30 hover:bg-[#38BDF8]/5 transition-all">
                      <FileText size={12} />
                      {content.services}
                    </button>
                  </div>
                </div>
              )}

              <div className="px-4 pb-2">
                <button onClick={() => setShowQuoteForm(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-xs font-semibold hover:from-emerald-500/30 hover:to-emerald-600/30 transition-all">
                  <FileText size={14} />
                  {content.getQuote}
                  <ArrowRight size={12} />
                </button>
              </div>

              <div className="p-3 border-t border-white/[0.06] bg-white/[0.01]">
                <div className="flex gap-2">
                  <input ref={inputRef} type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !isLoading && sendMessage()} placeholder={content.placeholder} disabled={isLoading} className="flex-1 h-10 px-4 rounded-xl bg-white/[0.03] border border-white/[0.06] text-[13px] text-white placeholder-white/30 focus:border-[#38BDF8]/30 focus:outline-none transition-all disabled:opacity-50" />
                  <button onClick={() => sendMessage()} disabled={!input.trim() || isLoading} className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] text-white disabled:opacity-40 hover:shadow-lg hover:shadow-[#38BDF8]/20 transition-all flex items-center justify-center">
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Floating button
export const PironeAIButton = ({ onClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.4 }}
      className="fixed bottom-6 right-6 z-[100]"
      data-testid="floating-assist-container"
    >
      <div className="relative flex items-stretch h-14 bg-[#0A0A0A] border border-white/[0.1] rounded-2xl shadow-2xl overflow-hidden hover:border-white/25 transition-colors">
        {/* Glow effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#25D366]/10 via-transparent to-[#38BDF8]/10 pointer-events-none" />

        {/* Left half — WhatsApp */}
        <a
          href="https://wa.me/15513548697?text=Merhaba%20PIRONE%2C%20yerel%20i%C5%9Fletmem%20i%C3%A7in%20bilgi%20almak%20istiyorum."
          target="_blank"
          rel="noopener noreferrer"
          className="relative group flex items-center gap-2.5 px-4 hover:bg-[#25D366]/10 transition-colors"
          data-testid="floating-whatsapp-btn"
          aria-label="Contact us on WhatsApp"
        >
          <div className="w-8 h-8 rounded-xl bg-[#25D366] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="text-white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </div>
          <span className="hidden sm:inline text-[13px] font-medium text-white/85 group-hover:text-white transition-colors whitespace-nowrap">WhatsApp</span>
        </a>

        {/* Divider */}
        <div className="w-px my-2 bg-white/10" />

        {/* Right half — PIRONE AI */}
        <button
          onClick={onClick}
          className="relative group flex items-center gap-2.5 px-4 hover:bg-[#38BDF8]/10 transition-colors"
          data-testid="floating-ai-btn"
          aria-label="Ask PIRONE AI"
        >
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#38BDF8] to-[#0EA5E9] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
            <Sparkles size={16} className="text-white" />
          </div>
          <span className="hidden sm:inline text-[13px] font-medium text-white/85 group-hover:text-white transition-colors whitespace-nowrap">PIRONE AI</span>
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse ml-0.5" />
        </button>
      </div>
    </motion.div>
  );
};

export default PironeAIChat;
